import React, { ClipboardEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { CheckCircleFilled, EyeInvisibleOutlined, EyeOutlined } from 'antd/icons';
import UnstyledButton from '@components/UI/UnstyledButton';
import { Input } from 'antd';
import { ANSI_SYMBOL, SPECIAL_CHARS_REGEX } from '@lib/passwordRules';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { debounce } from 'lodash';
import InputRequirements from '@components/FormInputs/InputRequirements';
import usePasswordVisibility from '@components/FormInputs/usePasswordVisibility';
import VisuallyHidden from '@components/UI/utilities/VisuallyHidden';

type PasswordValidatorProps = {
  password: string;
  setValidity: React.Dispatch<React.SetStateAction<boolean>>;
  isFocussed: boolean;
  // To help link to requirements component for screen reader accessibility
  requirementsId: string;
  setMissingSummary: React.Dispatch<React.SetStateAction<string>>;
};

const PasswordValidator = ({
  password,
  isFocussed,
  requirementsId,
  setMissingSummary,
  setValidity
}: PasswordValidatorProps) => {
  const dispatch = useDispatch();
  const [tooShort, setTooShort] = useState(true);
  const [missingUppercase, setMissingUppercase] = useState(true);
  const [missingLowercase, setMissingLowercase] = useState(true);
  const [missingSpecial, setMissingSpecial] = useState(true);
  const [missingDigit, setMissingDigit] = useState(true);
  const [containsSpace, setContainsSpace] = useState(true);
  const [isGoodPassword, setIsGoodPassword] = useState(false);

  const checkPassword = async (passwd: string) => {
    const result = await dispatch(actions.checkPassword(passwd), false);
    return result?.error?.code !== 412;
  };

  const handleValidPassword = async (passwd: string) => {
    const isGoodPassword = await checkPassword(passwd);
    setIsGoodPassword(isGoodPassword);
    setValidity(isGoodPassword);
  };

  const handleInvalidPassword = () => {
    setIsGoodPassword(false);
    setValidity(false);
  };

  const checkIfPasswordValid = async (passwd: string) => {
    const isValidPassword = checkBasicValidity(passwd);
    if (!passwd || !isValidPassword) {
      handleInvalidPassword();
      return;
    }

    await handleValidPassword(passwd);
  };

  const debouncedCheckIfPasswordValid = useMemo(() => debounce((passwd) => checkIfPasswordValid(passwd), 400), []);

  const checkBasicValidity = useCallback((passwd = '') => {
    const tooShort = passwd.length < 8;
    const missingDigit = !/\d/.test(passwd);
    const missingUppercase = !/[A-Z]/.test(passwd);
    const missingLowercase = !/[a-z]/.test(passwd);
    const missingSpecial = !SPECIAL_CHARS_REGEX.test(passwd);
    const hasSpace = /\s/.test(passwd) || passwd.length === 0;

    setMissingUppercase(missingUppercase);
    setMissingLowercase(missingLowercase);
    setMissingSpecial(missingSpecial);
    setMissingDigit(missingDigit);
    setTooShort(tooShort);
    setContainsSpace(hasSpace);

    return !(tooShort || missingDigit || missingLowercase || missingSpecial || missingUppercase || hasSpace);
  }, []);

  useEffect(() => {
    debouncedCheckIfPasswordValid(password || '');
  }, [checkBasicValidity, debouncedCheckIfPasswordValid, password]);

  // After blurring out of the password field, don't wait for a debounce, immediately trigger a password check
  useEffect(() => {
    if (!isFocussed && password) {
      checkIfPasswordValid(password).then();
    }
  }, [checkBasicValidity, checkIfPasswordValid, password, isFocussed]);

  const passwordRequirements = [
    { ok: !tooShort, description: '8 characters' },
    { ok: !containsSpace, description: 'No spaces' },
    { ok: !missingUppercase, description: '1 uppercase' },
    { ok: !missingLowercase, description: '1 lowercase' },
    { ok: !missingDigit, description: '1 number' },
    { ok: !missingSpecial, description: '1 special character' },
    { ok: isGoodPassword, description: 'Not common' }
  ];

  // Generate the summary of unfulfilled requirements on blur
  useEffect(() => {
    const missing = passwordRequirements
      .filter((req) => !req.ok)
      .map((req) => req.description)
      .join(', ');

    if (missing && password.length > 0) {
      setMissingSummary(`Password requirements not met: ${missing}`);
    } else if (!missing && password.length > 0) {
      setMissingSummary(`All password requirements are met.`);
    } else {
      setMissingSummary('');
    }
  }, [
    password,
    tooShort,
    containsSpace,
    missingUppercase,
    missingLowercase,
    missingDigit,
    missingSpecial,
    isGoodPassword
  ]);

  return <InputRequirements id={requirementsId} title="Requirements" inputRequirements={passwordRequirements} />;
};

const Ticked = styled(CheckCircleFilled)<{ ticked: boolean }>`
  opacity: ${({ ticked }) => (ticked ? 1 : 0)};
  color: green;
`;

type PasswordIconsProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  visible: boolean;
  valid: boolean;
};

const PasswordIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const PasswordIcons = ({ visible, valid, ...props }: PasswordIconsProps) => (
  <UnstyledButton data-testid="show" {...props}>
    <PasswordIconContainer>
      <Ticked ticked={valid} />
      <span>{visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}</span>
    </PasswordIconContainer>
  </UnstyledButton>
);

type PasswordProps = {
  onChange: (str: string) => void;
  setValid?: React.Dispatch<React.SetStateAction<boolean>>;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-required'?: React.AriaAttributes['aria-required'];
};

const Password = ({
  onChange = () => {},
  setValid = () => {},
  id = 'password-input-field',
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired
}: PasswordProps) => {
  const { t } = useTranslation('common');
  const [passwordValid, setPasswordValid] = useState(false);
  const [password, setPassword] = useState('');
  const [illegalCharacterPressed, setIllegalCharacterPressed] = useState(false);
  const [isFocussed, setIsFocussed] = useState(false);
  const [missingSummary, setMissingSummary] = useState('');

  const {
    visible: visiblePassword,
    toggleProps: passwordToggleProps,
    announcement: passwordVisibilityAnnouncement
  } = usePasswordVisibility();

  const requirementsId = `${id}-requirements`;
  const successId = `${id}-success`;
  const activeDescribedBy = passwordValid ? successId : requirementsId;
  const combinedDescribedBy = [ariaDescribedBy, activeDescribedBy].filter(Boolean).join(' ');

  useEffect(() => {
    setValid(passwordValid);
  }, [passwordValid]);

  useEffect(() => {
    if (illegalCharacterPressed) {
      const timer = setInterval(() => setIllegalCharacterPressed(false), 250);
      return () => clearInterval(timer);
    }

    return () => {};
  }, [illegalCharacterPressed]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setPassword(e.target.value);
    onChange(e.target.value);
  };

  const validateInputEvent = (event: any, str: string): void => {
    if (!ANSI_SYMBOL.test(str)) {
      event.preventDefault();
      setIllegalCharacterPressed(true);
    }
  };

  // Block illegal characters being entered on key down
  const onKeyDown: React.KeyboardEventHandler = (event) => {
    const { key, metaKey, ctrlKey } = event;
    // If Cmd (Mac) or Ctrl (Windows) is held down, allow the default browser action
    if (metaKey || ctrlKey) return;
    key.length === 1 && validateInputEvent(event, key);
  };

  // Block illegal characters being entered on paste
  const onPaste: ClipboardEventHandler<HTMLInputElement> = (event) => {
    const text = event.clipboardData.getData('text');
    text && validateInputEvent(event, text);
  };

  return (
    <div>
      <Input
        id={id}
        aria-describedby={combinedDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        type={visiblePassword ? 'text' : 'password'}
        value={password}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onChange={handleChange}
        onFocus={() => setIsFocussed(true)}
        onBlur={() => setIsFocussed(false)}
        className="login-input"
        placeholder={t('Password')}
        autoComplete="new-password"
        suffix={
          <>
            <PasswordIcons visible={visiblePassword} valid={passwordValid} {...passwordToggleProps} />
            {passwordVisibilityAnnouncement}
          </>
        }
        data-testid="password-input"
      />
      <PasswordValidator
        {...{
          password,
          isFocussed,
          setValidity: setPasswordValid,
          requirementsId,
          setMissingSummary
        }}
      />
      {/* Hidden area for the voiceover to read out */}
      <VisuallyHidden id={successId}>All password requirements met.</VisuallyHidden>
      <VisuallyHidden as="div" aria-live="polite">
        {missingSummary}
      </VisuallyHidden>
    </div>
  );
};

export default Password;
